/**
 * Standardized International & Domestic Phone Number Formatter
 * 
 * Accurately formats phone numbers with smart country code detection:
 * - India (+91):
 *     12-digit (918459391455)        -> '+91-84593 91455'
 *     10-digit mobile (8459391455)   -> '+91-84593 91455'
 *     11-digit leading 0 (08459391455)-> '+91-84593 91455'
 *     With + (+918459391455)         -> '+91-84593 91455'
 * - North America (+1):
 *     11-digit (14155552671)         -> '+1-415-555-2671'
 *     10-digit US/CA (4155552671)    -> '+1-415-555-2671'
 * - UK (+44):
 *     12-digit (447911123456)        -> '+44-7911 123456'
 * - UAE (+971):
 *     12-digit (971501234567)        -> '+971-50 123 4567'
 * - Australia (+61):
 *     11-digit (61412345678)         -> '+61-412 345 678'
 * - International 2 & 3-digit Country Codes (e.g. +65, +966, +49, +33, etc.)
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber || phoneNumber === "-") return "-";
  const str = String(phoneNumber).trim();
  const cleaned = str.replace(/\D/g, "");
  if (!cleaned) return str;

  const hasPlus = str.startsWith("+");

  // 1. If explicit leading '+' is provided, resolve directly with international country dial code
  if (hasPlus) {
    if (cleaned.startsWith("91") && cleaned.length === 12) {
      return `+91-${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
    }
    if (cleaned.startsWith("1") && cleaned.length === 11) {
      return `+1-${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    if (cleaned.startsWith("44") && cleaned.length === 12) {
      return `+44-${cleaned.slice(2, 6)} ${cleaned.slice(6)}`;
    }
    if (cleaned.startsWith("971")) {
      return `+971-${cleaned.slice(3, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
    }
    // 3-digit international codes
    const threeDigitCodes = ["966", "965", "974", "968", "973", "880", "977", "962", "961"];
    for (const code of threeDigitCodes) {
      if (cleaned.startsWith(code)) {
        const rest = cleaned.slice(code.length);
        const half = Math.ceil(rest.length / 2);
        return `+${code}-${rest.slice(0, half)} ${rest.slice(half)}`;
      }
    }
    // 2-digit international codes
    const twoDigitCodes = [
      "20", "27", "30", "31", "32", "33", "34", "36", "39", "40", "41", "43", "45", "46", "47", "48", "49",
      "51", "52", "53", "54", "55", "56", "57", "58", "60", "61", "62", "63", "64", "65", "66", "81", "82",
      "84", "86", "90", "92", "93", "94", "95", "98"
    ];
    for (const code of twoDigitCodes) {
      if (cleaned.startsWith(code)) {
        const rest = cleaned.slice(code.length);
        const half = Math.ceil(rest.length / 2);
        return `+${code}-${rest.slice(0, half)} ${rest.slice(half)}`;
      }
    }
    return `+${cleaned}`;
  }

  // 2. India (+91)
  // 12 digits starting with 91 (e.g. 918459391455) -> +91-84593 91455
  if (cleaned.length === 12 && cleaned.startsWith("91")) {
    return `+91-${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
  }

  // 11 digits starting with 0 followed by Indian mobile 6-9 (e.g. 08459391455)
  if (cleaned.length === 11 && cleaned.startsWith("0") && /^[6-9]/.test(cleaned[1])) {
    return `+91-${cleaned.slice(1, 6)} ${cleaned.slice(6)}`;
  }

  // Standard 10-digit Indian mobile starting with 6, 7, 8, 9 (e.g. 8459391455)
  if (cleaned.length === 10 && /^[6-9]/.test(cleaned)) {
    return `+91-${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }

  // 3. North America (+1: US, Canada)
  // 11 digits starting with 1 (e.g. 14155552671)
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `+1-${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  // 10-digit US/Canada area code (area codes start with 2-5, not 6-9)
  if (cleaned.length === 10 && !/^[6-9]/.test(cleaned)) {
    return `+1-${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  // 4. UK (+44) - 12 digits starting with 44
  if (cleaned.length === 12 && cleaned.startsWith("44")) {
    return `+44-${cleaned.slice(2, 6)} ${cleaned.slice(6)}`;
  }

  // 5. UAE (+971) - 12 digits starting with 971
  if (cleaned.length === 12 && cleaned.startsWith("971")) {
    return `+971-${cleaned.slice(3, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
  }

  // 6. Australia (+61) - 11 digits starting with 61
  if (cleaned.length === 11 && cleaned.startsWith("61")) {
    return `+61-${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
  }

  // 7. Generic 3-digit Country Codes with >= 11 digits
  const threeDigitCodes = ["966", "965", "974", "968", "973", "880", "977", "962", "961"];
  for (const code of threeDigitCodes) {
    if (cleaned.startsWith(code) && cleaned.length >= 11) {
      const rest = cleaned.slice(code.length);
      const half = Math.ceil(rest.length / 2);
      return `+${code}-${rest.slice(0, half)} ${rest.slice(half)}`;
    }
  }

  // 8. Generic 2-digit Country Codes with >= 11 digits
  const twoDigitCodes = [
    "20", "27", "30", "31", "32", "33", "34", "36", "39", "40", "41", "43", "45", "46", "47", "48", "49",
    "51", "52", "53", "54", "55", "56", "57", "58", "60", "61", "62", "63", "64", "65", "66", "81", "82",
    "84", "86", "90", "92", "93", "94", "95", "98"
  ];
  for (const code of twoDigitCodes) {
    if (cleaned.startsWith(code) && cleaned.length >= 11) {
      const rest = cleaned.slice(code.length);
      const half = Math.ceil(rest.length / 2);
      return `+${code}-${rest.slice(0, half)} ${rest.slice(half)}`;
    }
  }

  // Fallback
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }

  return phoneNumber;
};

export default formatPhoneNumber;
